import React, { useEffect, useState } from 'react';
import { FileText, Lock } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getProjects,
  Project,
  getBrandGuidesByProject,
  BrandGuide,
  listEligibleArticles,
  generateWhitepaper,
  EligibleArticle,
  WhitepaperGenerateRequest,
  WhitepaperGenerateResponse,
} from '../../services/api';
import './WhitepaperGenerator.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array
 *  (same helper as ProductsList.tsx / ProductEditor.tsx). */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

interface WhitepaperGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-fills the article picker when opened via a deep link from
   *  ContentCatalogue (?whitepaper_from=<task_id>&whitepaper_project=<project_id>). */
  initialTaskId?: string | null;
  initialProjectId?: string | null;
  onGenerated?: (product: WhitepaperGenerateResponse) => void;
}

type ImagesMode = 'none' | 'reuse' | 'generate';

const WhitepaperGenerator: React.FC<WhitepaperGeneratorProps> = ({
  isOpen,
  onClose,
  initialTaskId,
  initialProjectId,
  onGenerated,
}) => {
  const { isTrialUser } = usePermissions();

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [articles, setArticles] = useState<EligibleArticle[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [taskId, setTaskId] = useState<string>('');

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');

  const [branded, setBranded] = useState(true);
  const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
  const [brandGuideId, setBrandGuideId] = useState<string>('');

  const [includeToc, setIncludeToc] = useState(true);
  const [includeCtaPage, setIncludeCtaPage] = useState(true);
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  const [includeChecklist, setIncludeChecklist] = useState(false);
  const [checklistPosition, setChecklistPosition] = useState<'after_body' | 'before_cta'>('after_body');

  const [imagesMode, setImagesMode] = useState<ImagesMode>('reuse');
  const [sectionImageCount, setSectionImageCount] = useState(3);
  const [includeCover, setIncludeCover] = useState(true);

  const [gated, setGated] = useState(true);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Reset to a clean slate every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setGenerating(false);
    setTitle('');
    setSubtitle('');
    setAuthor('');
    setBranded(true);
    setBrandGuideId('');
    setIncludeToc(true);
    setIncludeCtaPage(true);
    setCtaText('');
    setCtaUrl('');
    setIncludeChecklist(false);
    setChecklistPosition('after_body');
    setImagesMode('reuse');
    setSectionImageCount(3);
    setIncludeCover(true);
    setGated(true);
    setTaskId(initialTaskId || '');
    setProjectId(initialProjectId || '');

    getProjects(false)
      .then(setProjects)
      .catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load eligible articles whenever the project changes
  useEffect(() => {
    if (!isOpen || !projectId) {
      setArticles([]);
      return;
    }
    setArticlesLoading(true);
    listEligibleArticles(projectId)
      .then(list => setArticles(list))
      .catch(() => setArticles([]))
      .finally(() => setArticlesLoading(false));
  }, [isOpen, projectId]);

  // Load brand guides whenever the project changes (only needed when branded,
  // but fetched eagerly so toggling "branded" on doesn't need a second round-trip)
  useEffect(() => {
    if (!isOpen || !projectId) {
      setBrandGuides([]);
      setBrandGuideId('');
      return;
    }
    getBrandGuidesByProject(projectId)
      .then(list => {
        setBrandGuides(list);
        const def = list.find(g => g.is_default);
        setBrandGuideId(def ? def.brand_guide_id : (list[0]?.brand_guide_id || ''));
      })
      .catch(() => setBrandGuides([]));
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  const selectedArticle = articles.find(a => a.task_id === taskId);
  const canGenerate = Boolean(projectId && taskId) && !generating;

  const handleGenerate = async () => {
    if (!projectId || !taskId) return;
    setGenerating(true);
    setError('');
    try {
      const body: WhitepaperGenerateRequest = {
        task_id: taskId,
        project_id: projectId,
        brand_guide_id: branded ? (brandGuideId || null) : null,
        product_id: null,
        title: title.trim() || null,
        subtitle: subtitle.trim() || null,
        author: author.trim() || null,
        branded,
        layout: 'report',
        cover_style: 'brand_block',
        include_toc: includeToc,
        include_cta_page: includeCtaPage,
        cta_text: includeCtaPage ? (ctaText.trim() || null) : null,
        cta_url: includeCtaPage ? (ctaUrl.trim() || null) : null,
        include_checklist: includeChecklist,
        checklist_position: checklistPosition,
        force_checklist: false,
        images: {
          mode: imagesMode,
          count: imagesMode === 'none' ? 0 : sectionImageCount,
          include_cover: imagesMode === 'none' ? false : includeCover,
        },
        gated,
      };
      const product = await generateWhitepaper(body);
      onGenerated?.(product);
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate whitepaper. Please try again.'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !generating && onClose()}>
      <div className="modal-panel whitepaper-modal-panel" onClick={e => e.stopPropagation()}>
        <div className="whitepaper-modal-header">
          <FileText size={20} />
          <h3>New Whitepaper</h3>
        </div>
        <p className="whitepaper-modal-subtitle">
          Turn a completed article into a branded PDF whitepaper you can deliver as a gated or ungated download.
        </p>

        {/* Project + Article */}
        <div className="whitepaper-form-section">
          <div className="modal-form-group">
            <label>Project</label>
            <select value={projectId} onChange={e => { setProjectId(e.target.value); setTaskId(''); }}>
              <option value="">Select a project…</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="modal-form-group">
            <label>Article</label>
            {projectId && articlesLoading ? (
              <div className="whitepaper-hint">Loading eligible articles…</div>
            ) : projectId && articles.length === 0 ? (
              <div className="whitepaper-hint">
                No eligible completed articles in this project yet (needs a completed article of sufficient length).
              </div>
            ) : (
              <select value={taskId} onChange={e => setTaskId(e.target.value)} disabled={!projectId}>
                <option value="">Select an article…</option>
                {articles.map(a => (
                  <option key={a.task_id} value={a.task_id}>
                    {a.name} ({a.word_count.toLocaleString()} words{a.stream ? ` · ${a.stream}` : ''})
                  </option>
                ))}
              </select>
            )}
            {taskId && initialTaskId === taskId && !selectedArticle && (
              <div className="whitepaper-hint">
                The article you came from isn't in this project's eligible list — pick another.
              </div>
            )}
          </div>
        </div>

        {/* Title / Subtitle / Author */}
        <div className="whitepaper-form-section">
          <div className="modal-form-group">
            <label>Title (optional — defaults to the article title)</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={selectedArticle?.name || 'Whitepaper title'} />
          </div>
          <div className="whitepaper-form-row">
            <div className="modal-form-group">
              <label>Subtitle</label>
              <input type="text" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Optional" />
            </div>
            <div className="modal-form-group">
              <label>Author</label>
              <input type="text" value={author} onChange={e => setAuthor(e.target.value)} placeholder="Optional" />
            </div>
          </div>
        </div>

        {/* Style */}
        <div className="whitepaper-form-section">
          <h4 className="whitepaper-section-title">Style</h4>
          <label className="whitepaper-toggle-row">
            <input type="checkbox" checked={branded} onChange={e => setBranded(e.target.checked)} />
            <span>Use brand guide styling</span>
          </label>
          {branded && (
            <div className="whitepaper-form-row">
              <div className="modal-form-group">
                <label>Brand guide</label>
                <select value={brandGuideId} onChange={e => setBrandGuideId(e.target.value)} disabled={!projectId || brandGuides.length === 0}>
                  {brandGuides.length === 0 && <option value="">No brand guides for this project — house style</option>}
                  {brandGuides.map(g => (
                    <option key={g.brand_guide_id} value={g.brand_guide_id}>
                      {g.brand_guide_name}{g.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-form-group">
                <label>Cover style</label>
                <select value="brand_block" disabled>
                  <option value="brand_block">Brand block</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="whitepaper-form-section">
          <h4 className="whitepaper-section-title">Sections</h4>
          <label className="whitepaper-toggle-row">
            <input type="checkbox" checked={includeToc} onChange={e => setIncludeToc(e.target.checked)} />
            <span>Include table of contents</span>
          </label>
          <label className="whitepaper-toggle-row">
            <input type="checkbox" checked={includeCtaPage} onChange={e => setIncludeCtaPage(e.target.checked)} />
            <span>Include a call-to-action page</span>
          </label>
          {includeCtaPage && (
            <div className="whitepaper-form-row">
              <div className="modal-form-group">
                <label>CTA text</label>
                <input type="text" value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="e.g. Book a free consultation" />
              </div>
              <div className="modal-form-group">
                <label>CTA URL</label>
                <input type="text" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://…" />
              </div>
            </div>
          )}

          <label className="whitepaper-toggle-row">
            <input type="checkbox" checked={includeChecklist} onChange={e => setIncludeChecklist(e.target.checked)} />
            <span>Include an action checklist</span>
          </label>
          {includeChecklist && (
            <div className="modal-form-group">
              <label>Checklist position</label>
              <select value={checklistPosition} onChange={e => setChecklistPosition(e.target.value as 'after_body' | 'before_cta')}>
                <option value="after_body">After the article body</option>
                <option value="before_cta">Before the CTA page</option>
              </select>
            </div>
          )}
        </div>

        {/* Images */}
        <div className="whitepaper-form-section">
          <h4 className="whitepaper-section-title">Images</h4>
          <div className="whitepaper-radio-group">
            <label className="whitepaper-toggle-row">
              <input type="radio" name="images-mode" checked={imagesMode === 'none'} onChange={() => setImagesMode('none')} />
              <span>None</span>
            </label>
            <label className="whitepaper-toggle-row">
              <input type="radio" name="images-mode" checked={imagesMode === 'reuse'} onChange={() => setImagesMode('reuse')} />
              <span>Use this article's images</span>
            </label>
            <label className="whitepaper-toggle-row">
              <input
                type="radio"
                name="images-mode"
                checked={imagesMode === 'generate'}
                onChange={() => setImagesMode('generate')}
                disabled={isTrialUser}
              />
              <span>
                Generate new images
                {isTrialUser && (
                  <span className="whitepaper-upgrade-hint">
                    <Lock size={12} /> Upgrade to generate new images for whitepapers
                  </span>
                )}
              </span>
            </label>
          </div>

          {imagesMode !== 'none' && (
            <div className="whitepaper-form-row">
              <div className="modal-form-group">
                <label>Section images ({sectionImageCount})</label>
                <input
                  type="range"
                  min={0}
                  max={3}
                  step={1}
                  value={sectionImageCount}
                  onChange={e => setSectionImageCount(parseInt(e.target.value, 10))}
                />
              </div>
              <label className="whitepaper-toggle-row" style={{ alignSelf: 'center' }}>
                <input type="checkbox" checked={includeCover} onChange={e => setIncludeCover(e.target.checked)} />
                <span>Include cover image</span>
              </label>
            </div>
          )}
        </div>

        {/* Gated */}
        <div className="whitepaper-form-section">
          <label className="whitepaper-toggle-row">
            <input type="checkbox" checked={gated} onChange={e => setGated(e.target.checked)} />
            <span>Require a form submission to download</span>
          </label>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-actions">
          <GlassButton variant="secondary" onClick={onClose} disabled={generating}>
            Cancel
          </GlassButton>
          <GlassButton variant="primary" onClick={handleGenerate} loading={generating} disabled={!canGenerate} icon={<FileText size={16} />}>
            Generate Whitepaper
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default WhitepaperGenerator;
